import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppBar, Body, ErrorBox, Label, ListCard, Screen, Spinner, cx } from '../components/ui';
import { useGroup, useGroupToken } from '../hooks/useGroup';
import { api, ApiError } from '../lib/api';
import type { Member } from '../lib/api';
import { initial } from '../lib/format';

/**
 * メンバー管理（画面案8 / §4.2）。
 *
 * 履歴があると削除できない仕様は、禁止として書くより理由を添えたほうが伝わる。
 * 卓に入らなくなった人は「候補から外す」で入力画面から消える。
 */
export function MembersPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const token = useGroupToken(groupId);
  const group = useGroup(groupId, token);
  const queryClient = useQueryClient();

  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['group', groupId] });

  const add = useMutation({
    mutationFn: (name: string) => api.createMember(groupId!, token!, name),
    onSuccess: () => {
      setNewName('');
      setError(null);
      void refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '追加できませんでした'),
  });

  const update = useMutation({
    mutationFn: ({ memberId, patch }: { memberId: string; patch: Parameters<typeof api.updateMember>[3] }) =>
      api.updateMember(groupId!, token!, memberId, patch),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      void refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '更新できませんでした'),
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => api.deleteMember(groupId!, token!, memberId),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '削除できませんでした'),
  });

  if (!token) {
    return (
      <Screen>
        <AppBar title="メンバー" backTo="/" />
        <Body>
          <ErrorBox message="この端末に参加情報がありません。共有URLから開いてください。" />
        </Body>
      </Screen>
    );
  }

  if (group.isLoading || !group.data) {
    return (
      <Screen>
        <AppBar title="メンバー" backTo={`/g/${groupId}`} />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  const members = [...group.data.members].sort((a, b) => a.order - b.order);
  const busy = add.isPending || update.isPending || remove.isPending;

  function startEdit(m: Member) {
    setEditing(m.memberId);
    setEditName(m.name);
  }

  return (
    <Screen>
      <AppBar title="メンバー" subtitle={`${members.length}人`} backTo={`/g/${groupId}`} />

      <Body className="gap-2.5">
        {error && <ErrorBox message={error} />}

        <ListCard className="py-1">
          {members.map((m, i) => (
            <div
              key={m.memberId}
              className={cx('flex items-center gap-3 py-3', i > 0 && 'border-t border-line')}
            >
              <span
                className={cx(
                  'grid size-8 shrink-0 place-items-center rounded-lg bg-bone text-sm font-bold text-felt',
                  !m.active && 'opacity-45',
                )}
              >
                {initial(m.name)}
              </span>

              {editing === m.memberId ? (
                <>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={16}
                    autoFocus
                    className="min-w-0 flex-1 rounded-lg border border-felt bg-white px-2 py-1.5 text-[14.5px] outline-none"
                  />
                  <button
                    type="button"
                    disabled={busy || !editName.trim()}
                    onClick={() =>
                      update.mutate({ memberId: m.memberId, patch: { name: editName.trim() } })
                    }
                    className="shrink-0 text-[13px] font-bold text-felt disabled:text-ink-faint"
                  >
                    保存
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(null)}
                    className="shrink-0 text-[13px] text-ink-faint"
                  >
                    やめる
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => startEdit(m)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className={cx('truncate text-[14.5px] font-bold', !m.active && 'opacity-45')}>
                      {m.name}
                    </div>
                    {!m.active && (
                      <div className="text-[11px] text-ink-faint">入力候補から除外中</div>
                    )}
                  </button>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      update.mutate({ memberId: m.memberId, patch: { active: !m.active } })
                    }
                    className="shrink-0 text-[12px] text-ink-soft disabled:opacity-40"
                  >
                    {m.active ? '候補から外す' : '候補に戻す'}
                  </button>

                  <button
                    type="button"
                    disabled={busy}
                    aria-label={`${m.name}を削除`}
                    onClick={() => remove.mutate(m.memberId)}
                    className="shrink-0 px-1 text-[15px] text-ink-faint disabled:opacity-40"
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          ))}
        </ListCard>

        {/* --- 追加 --- */}
        {members.length < 20 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim()) add.mutate(newName.trim());
            }}
            className="flex items-stretch gap-2"
          >
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={16}
              placeholder="名前を入力して追加"
              className="min-w-0 flex-1 rounded-lg border border-line bg-white px-3 py-2.5 text-[15px] outline-none focus:border-felt"
            />
            {/*
              共通の Button は w-full と厚めの縦パディングを前提にしているため、
              入力欄と横に並べると高さが揃わない。ここは行の高さに合わせる。
            */}
            <button
              type="submit"
              disabled={!newName.trim() || busy}
              className="shrink-0 rounded-lg bg-felt px-5 text-[14px] font-bold text-bone-hi transition active:scale-[0.99] disabled:bg-off disabled:text-off-ink"
            >
              追加
            </button>
          </form>
        )}

        <Label className="mt-1 leading-loose">
          対局の記録があるメンバーは削除できません。卓に入らなくなった人は、候補から外すと入力画面がすっきりします。
        </Label>
      </Body>
    </Screen>
  );
}
